import { type ResourcesConfig } from 'aws-amplify'

// Provider list per env MUST mirror the CDK `SOCIAL_PROVIDERS_BY_STAGE` config so the frontend never
// offers a provider the Cognito user pool client does not actually support. Amplify accepts the
// built-in provider strings plus `{ custom: 'Name' }` for generic OIDC providers (e.g. LinkedIn).
// Add a provider here only once it is enabled (and its secret exists) for that stage in CDK.
type AmplifyOAuthProvider = 'Amazon' | 'Google' | 'Facebook' | 'Apple' | { custom: string }

// `domain` MUST match the Cognito Hosted UI domain CDK produces for the stage:
//   - prod: custom branded domain `auth.freepatternwarehouse.com`
//   - beta: AWS-hosted prefix (custom domain gated off until its cert is provisioned)
//   - dev:  AWS-hosted prefix (localhost; no custom domain)
const ENV_CONFIGS: Record<
  'dev' | 'beta' | 'prod',
  {
    userPoolId: string
    userPoolClientId: string
    domain: string
    redirectSignIn: string
    redirectSignOut: string
    providers: AmplifyOAuthProvider[]
  }
> = {
  dev: {
    userPoolId: 'us-west-2_HSNNacxcC',
    userPoolClientId: '5gh30bpinv1cefcrm4d3b09ugs',
    domain: 'dev-mbenav-pattern-warehouse-v3.auth.us-west-2.amazoncognito.com',
    redirectSignIn: 'https://localhost:3000/callback',
    redirectSignOut: 'https://localhost:3000/',
    providers: ['Amazon', 'Google', 'Facebook'],
  },
  beta: {
    userPoolId: 'us-west-2_oGWlmo7i8',
    userPoolClientId: '3v8o63tmrd39opkpo48crjuhr9',
    // Branded custom domain (matches prod's pattern). The beta pool's Hosted UI is served from the
    // custom domain `auth.beta.freepatternwarehouse.com` (Cognito status ACTIVE, backed by CloudFront).
    // The old AWS-hosted prefix `beta-pattern-warehouse-v3.auth.us-west-2.amazoncognito.com` no longer
    // exists on the pool, so pointing here previously produced an empty Hosted-UI page. REQUIRES the
    // `auth.beta` DNS alias record in the prod-account zone (see manual-configurations.md).
    domain: 'auth.beta.freepatternwarehouse.com',
    redirectSignIn: 'https://www.beta.freepatternwarehouse.com/callback',
    redirectSignOut: 'https://www.beta.freepatternwarehouse.com/',
    providers: ['Amazon', 'Google'],
  },
  prod: {
    userPoolId: 'us-west-2_RfRIaeqJb',
    userPoolClientId: '5cbruq95okldaqn15bir2r8998',
    domain: 'auth.freepatternwarehouse.com',
    redirectSignIn: 'https://www.freepatternwarehouse.com/callback',
    redirectSignOut: 'https://www.freepatternwarehouse.com/',
    providers: ['Amazon', 'Google'],
  },
} as const

const ENV = (import.meta.env.VITE_ENV || 'dev') as keyof typeof ENV_CONFIGS
const config = ENV_CONFIGS[ENV]

/** Federated providers enabled for the current environment (used by the login UI). */
export const enabledAuthProviders: AmplifyOAuthProvider[] = [...config.providers]

export const authConfig: ResourcesConfig['Auth'] = {
  Cognito: {
    userPoolId: config.userPoolId,
    userPoolClientId: config.userPoolClientId,
    loginWith: {
      oauth: {
        domain: config.domain,
        scopes: ['openid', 'profile', 'email'],
        redirectSignIn: [config.redirectSignIn],
        redirectSignOut: [config.redirectSignOut],
        responseType: 'code',
        providers: config.providers,
      },
    },
  },
}
